import { Inject, Injectable } from '@angular/core';
import {
    DisruptionMessage,
    DisruptionMessageType,
    DisruptionMessageEffectType,
    DisruptionMessageEntityType
} from '../ridango.models';
import Env from '../../env';

@Injectable({
    providedIn: 'root'
})
export class MessageTypeService {
    private readonly ROUTE_TYPES = ['ROUTE', 'STOP_ON_ROUTE', 'STOP_ON_TRIP'] as const;

    constructor(@Inject('ENV') private env: Env) {}

    private hasEntityType(message: DisruptionMessage, type: string): boolean {
        return message.informedEntities.some((entity) => (entity.type as string) === type);
    }

    private hasRouteInfo(message: DisruptionMessage): boolean {
        return message.informedEntities.some(
            (entity) =>
                entity.type === DisruptionMessageEntityType.ROUTE ||
                (entity.type as string) === 'STOP_ON_ROUTE' ||
                (entity.type as string) === 'STOP_ON_TRIP' ||
                !!entity.routeShortName?.trim()
        );
    }

    private hasStopInfo(message: DisruptionMessage): boolean {
        return message.informedEntities.some(
            (entity) =>
                entity.type === DisruptionMessageEntityType.STOP ||
                (entity.type as string) === 'STOP_ON_ROUTE' ||
                (entity.type as string) === 'STOP_ON_TRIP' ||
                entity.stopId != null
        );
    }

    private hasRouteEntityWithRouteName(message: DisruptionMessage): boolean {
        return message.informedEntities.some(
            (entity) =>
                ((entity.type as string) === 'STOP_ON_ROUTE' || (entity.type as string) === 'STOP_ON_TRIP') &&
                !!entity.routeShortName?.trim()
        );
    }

    private hasStopEntityWithStopId(message: DisruptionMessage): boolean {
        return message.informedEntities.some(
            (entity) =>
                ((entity.type as string) === 'STOP_ON_ROUTE' || (entity.type as string) === 'STOP_ON_TRIP') &&
                entity.stopId != null
        );
    }

    determineMessageType(message: DisruptionMessage): DisruptionMessageType {
        if (this.env.WMB?.DISRUPTION_MESSAGES && message.messageType) {
            return message.messageType;
        }

        if (this.isBusAndStopDisruption(message)) {
            return DisruptionMessageType.ALERT_BUS_AND_STOP;
        }

        if (this.isBusDisruption(message)) {
            return DisruptionMessageType.ALERT_BUS;
        }

        if (this.isStopDisruption(message)) {
            return DisruptionMessageType.ALERT_STOP;
        }

        if (this.isGenericDisruption(message)) {
            return DisruptionMessageType.ALERT_GENERIC;
        }
    }

    isGenericDisruption(message: DisruptionMessage): boolean {
        if (message.informedEntities.length === 0) {
            return true;
        }

        return message.informedEntities.every((entity) => entity.type === null || entity.type === undefined);
    }

    isBusAndStopDisruption(message: DisruptionMessage): boolean {
        if (this.env.WMB?.DISRUPTION_MESSAGES && message.messageType) {
            return message.messageType === DisruptionMessageType.ALERT_BUS_AND_STOP;
        }

        return this.hasRouteInfo(message) && this.hasStopInfo(message);
    }

    isBusDisruption(message: DisruptionMessage): boolean {
        if (message.informedEntities.length === 0) {
            return false;
        }

        if (this.env.WMB?.DISRUPTION_MESSAGES) {
            const hasRouteType = message.informedEntities.some(
                (entity) => entity.type === DisruptionMessageEntityType.ROUTE
            );
            if (hasRouteType) {
                return true;
            }

            if (message.messageType) {
                return message.messageType === DisruptionMessageType.ALERT_BUS;
            }
        }

        // Check for combined bus+stop disruption first to avoid circular dependency
        if (this.hasRouteInfo(message) && this.hasStopInfo(message)) {
            return false;
        }

        if (this.hasEntityType(message, 'ROUTE') || this.hasRouteEntityWithRouteName(message)) {
            return true;
        }

        if (this.env.APP_NAME === 'sormland') {
            return message.informedEntities.every((entity) => entity.stopId === null && entity.routeShortName !== null);
        }

        return message.informedEntities.every((entity) => entity.routeType === 'B' && entity.stopId === null);
    }

    isStopDisruption(message: DisruptionMessage): boolean {
        if (message.informedEntities.length === 0) {
            return false;
        }

        if (this.env.WMB?.DISRUPTION_MESSAGES) {
            const hasStopType = message.informedEntities.some(
                (entity) => entity.type === DisruptionMessageEntityType.STOP
            );
            if (hasStopType) {
                return true;
            }

            if (message.messageType) {
                return message.messageType === DisruptionMessageType.ALERT_STOP;
            }
        }

        // Check for combined bus+stop disruption first to avoid circular dependency
        if (this.hasRouteInfo(message) && this.hasStopInfo(message)) {
            return false;
        }

        if (this.hasEntityType(message, 'STOP') || this.hasStopEntityWithStopId(message)) {
            return true;
        }

        return message.informedEntities.every((entity) => entity.stopId !== null);
    }

    isEffectDisruption(message: DisruptionMessage): string | null {
        if (message.effect && message.effect !== DisruptionMessageEffectType.UNKNOWN_EFFECT) {
            return 'DISRUPTION_EFFECT_' + message.effect;
        }

        return null;
    }
}
